import React, { useEffect, useRef, useState } from 'react';
import { Box, IconButton } from '@mui/material';
import { useMytaverseEvent } from '../../../providers';
import { useMytaStreams } from './MytaStreamsProvider';
import LoadingCinema from './LoadingCinema';
import SpotNoticeBanner from './SpotNoticeBanner';
import AfkPrompt from './AfkPrompt';
import StreamFpsBadge from './StreamFpsBadge';
import { useEventContext } from '@providers/EventProvider';
import { useConference } from '../Dolby';
import { useApplyStreamCursor } from '@components/StreamCursor';
import { useStyles } from '../CoreWeave/styles';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen';
import { AnalyticsType } from '@interfaces/analytics';
import { CurrentScreenEnum } from '@providers/MytaverseProvider/interfaces';
import { useAdvanceScreen } from './useStreamStage';

export const PixelStreamingWrapper = () => {
  const videoParent = useRef<HTMLDivElement>(null);
  // Latched, never un-latched: the world has been on screen at least once. `currentRoom` is derived state and
  // the entry handshake blanks it for a beat (see MytaverseEventProvider's seated-room restore), so keying the
  // container and the loader off it directly tore the live video out of the DOM and flashed the loading screen
  // back over a running stream. Once the picture has been shown, a transient room gap must not be visible.
  const [worldShown, setWorldShown] = useState(false);
  const {
    currentRoom,
    sharingWindowFullsceen,
    setSharingWindowFullscreen,
    handleMinimizeSharingScreen,
    isMinimizedScreenSharing,
    trackEventAnalytics,
  } = useMytaverseEvent();
  const {
    mytaLoading,
    dataChannelOpened,
    playStream,
    rootElement,
    reconnecting,
  } = useMytaStreams();
  const { setIsEventRun } = useEventContext();

  const { screenMediaStreams } = useConference();
  const advanceScreen = useAdvanceScreen();
  const classes = useStyles();

  // MV-2116 — the cursor UE reports rides on the library's own <video>, which
  // lives inside `rootElement`, not inside the Box below. Keyed off
  // `rootElement` so a KI-37 peer rebuild (a fresh videoElementParent) re-applies
  // it to the element that replaced the old one.
  useApplyStreamCursor(rootElement);

  useEffect(() => {
    if (
      mytaLoading ||
      !videoParent.current ||
      !rootElement ||
      !dataChannelOpened ||
      !playStream
    ) {
      return;
    }

    // replaceChildren, not appendChild: the KI-37 watchdog can rebuild the PixelStreaming instance on the
    // same lease, and each rebuild brings its own videoElementParent. Appending would stack a dead video
    // element over the live one — the user would keep staring at the frozen first attempt.
    videoParent.current.replaceChildren(rootElement);
    // The library measured its input rect while videoElementParent was still detached (0×0): the playStream
    // dispatch below fires before this mount whenever the room resolves late (fresh entry), so nothing
    // re-synced after the element actually landed in the DOM — mouse/click coords went to UE normalized
    // against nothing until the user resized the window by hand (measured 2026-08-14). Re-fire the resize
    // HERE, after every real (re)mount: the lib's input coord translator hangs off it, and so does the KI-70
    // capped resolution sender in MytaStreamsProvider (MatchViewportRes itself is off since KI-70).
    window.dispatchEvent(new Event('resize'));
  }, [
    mytaLoading,
    dataChannelOpened,
    rootElement,
    videoParent,
    playStream,
    currentRoom,
  ]);

  useEffect(() => {
    if (playStream) {
      setIsEventRun(true);
      trackEventAnalytics(AnalyticsType.TeleportToWorldClick);
      window.dispatchEvent(new Event('resize'));
    }
  }, [playStream, trackEventAnalytics]);

  useEffect(() => {
    if (playStream && currentRoom) {
      setWorldShown(true);
      advanceScreen(CurrentScreenEnum.WORLD);
    }
  }, [playStream, currentRoom, advanceScreen]);

  return (
    <>
      {/* Spot per client: the reclaim notice / handoff banner sits above everything, loader included — a reclaim
          after the world has been shown would otherwise happen behind a frozen frame with no loader to explain it. */}
      <SpotNoticeBanner />
      {/* The AFK presence check and the session-ended notice. Above everything for the same reason the
          banner is: the question can be raised while the loading cinema is still up (an entry nobody came
          back to), and a prompt the loader covers is a prompt nobody can answer. */}
      <AfkPrompt />
      {/* The per-client frames-per-second readout (myta-streams clients registry). Renders nothing unless the
          lease says this client shows it, which is off for everyone by default. */}
      <StreamFpsBadge />
      {currentRoom || worldShown ? (
        <Box
          id="mytaStreamsWrapper"
          sx={{
            width: '100vw',
            height: '100vh',
            position: 'relative',
            zIndex: playStream ? 10 : -10,
            display: playStream ? 'block' : 'hidden',
          }}
          ref={videoParent}
        ></Box>
      ) : null}

      {currentRoom && screenMediaStreams.length && !isMinimizedScreenSharing ? (
        <>
          <IconButton
            sx={classes.fullscreen}
            id="gameFull"
            onClick={setSharingWindowFullscreen}
          >
            {sharingWindowFullsceen ? (
              <FullscreenExitIcon />
            ) : (
              <FullscreenIcon />
            )}
          </IconButton>
          <IconButton
            sx={classes.fullscreenCollapse}
            id="gameCollapse"
            onClick={handleMinimizeSharingScreen}
          >
            <CloseFullscreenIcon />
          </IconButton>
        </>
      ) : null}

      {/* `playStream` alone is not "the user can see the world": the container above is only rendered once a
          room is resolved, so a stream that is decoding into a detached element would drop the loader onto a
          blank grey page — measured on a reload rebind 2026-08-12, video playing at 30 fps with no
          #mytaStreamsWrapper in the DOM. Hold the loader until the picture has actually been on screen.
          After that only `reconnecting` brings it back — a spot reclaim, a dead node, a peer rebuild — because
          the alternative the user actually got was a black rectangle with the hub chrome still on it while the
          ladder looked for a new seat (measured 2026-09-11 on a reclaim). It is an OVERLAY: the container above
          stays mounted (`worldShown` is never un-latched) so the video element is not torn out of the DOM and
          re-adopted, and the cinema's own `zIndex: 30` puts it over the stream's 10. */}
      {!worldShown || reconnecting ? <LoadingCinema /> : null}
    </>
  );
};
